/**
 * Site About → DoubleTake tab (ENG-2611).
 *
 * Who can do what, mirroring admin (ENG-2708):
 * - Staff can turn site DoubleTake (`vlm_enabled`) on and off on any site.
 * - Customers can turn it on only when a group above the site has DoubleTake
 *   available (`doubletake_available` on `customer/{id}/about/`), and can
 *   always turn it off.
 * - The FP (receiver) gate (`vlm_gate_enabled`) is staff-only. It is written
 *   through `PATCH customer/{id}/`, not About, and customers see it read-only
 *   (ENG-2728 decides whether that changes).
 */

/** `clickedTab` value for the DoubleTake tab; "one"–"nine" are taken. */
export const DOUBLE_TAKE_TAB = "ten";

export interface DoubleTakeSiteFields {
  vlm_enabled?: boolean;
  doubletake_available?: boolean;
}

export interface DoubleTakeState {
  vlmEnabled: boolean;
  gateEnabled: boolean;
}

/**
 * Staff see the tab on every site. Customers see it when their group has
 * DoubleTake available, or when the site already has it on (so a site whose
 * group lost access can still turn it off). All behind `showVlmFilters`.
 */
export const shouldShowDoubleTakeTab = (
  showVlmFilters: boolean,
  siteInfo: DoubleTakeSiteFields | undefined,
  isStaff: boolean
): boolean =>
  showVlmFilters &&
  (isStaff ||
    Boolean(siteInfo?.doubletake_available) ||
    Boolean(siteInfo?.vlm_enabled));

/** Whether the DoubleTake switch is usable from its saved state. */
export const canToggleDoubleTake = (
  savedVlmEnabled: boolean,
  siteInfo: DoubleTakeSiteFields | undefined,
  isStaff: boolean
): boolean =>
  isStaff || savedVlmEnabled || Boolean(siteInfo?.doubletake_available);

export interface DoubleTakePatches {
  /** Body for `PATCH customer/{id}/about/`, or null when unchanged. */
  about: { vlm_enabled: boolean } | null;
  /** Body for `PATCH customer/{id}/` (staff only), or null when unchanged. */
  gate: { vlm_gate_enabled: boolean } | null;
}

/**
 * The requests a save needs. The gate is only ever sent by staff, so a
 * customer save can't trip the staff-only 403.
 */
export const buildDoubleTakePatches = (
  saved: DoubleTakeState,
  draft: DoubleTakeState,
  isStaff: boolean
): DoubleTakePatches => ({
  about:
    draft.vlmEnabled !== saved.vlmEnabled
      ? { vlm_enabled: draft.vlmEnabled }
      : null,
  gate:
    isStaff && draft.gateEnabled !== saved.gateEnabled
      ? { vlm_gate_enabled: draft.gateEnabled }
      : null,
});
